/**
 * Caja a la vista en el punto de venta (pedido del cliente 10-10-2026: «si la caja está cerrada no me lo dice hasta el
 * final»). El estado de la caja se ve al entrar a vender y desde ahí mismo se abre, sin salir de la venta:
 *  - `FormAbrirCaja`: el mismo formulario de la pantalla Caja (caja, fondo fijo propuesto, aviso si se cambia);
 *  - `DialogoAbrirCaja`: ese formulario en un diálogo;
 *  - `BarraCaja`: la franja del punto de venta que dice cómo está la caja y qué se puede hacer.
 */
import { abrirCajaInput, fechaCaracas, Money, type AbrirCajaInput, type CajaDTO, type SesionCajaDTO, type SucursalDTO } from '@mg/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, Building2, Lock, TriangleAlert, Wallet } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { get, mensajeDeError, post } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { pedirCambioDeSucursal } from '@/lib/cambio-sucursal.ts';
import { normalizarDecimal } from '@/lib/montos.ts';
import { claves } from '@/lib/query.ts';
import { formatearFechaHora } from '@/lib/utils.ts';

type SucursalMia = SucursalDTO & { cajas: CajaDTO[] };

/** Abre la caja del usuario. Propone el fondo fijo de la caja elegida; si se abre con otro monto, la dueña se entera. */
export function FormAbrirCaja({ alAbrir }: { alAbrir?: (s: SesionCajaDTO) => void }) {
  const { recargar } = useAuth();
  const qc = useQueryClient();
  const mia = useQuery({ queryKey: claves.sucursalMia, queryFn: () => get<SucursalMia | null>('/sucursales/mia') });
  const form = useForm<AbrirCajaInput>({ resolver: zodResolver(abrirCajaInput), defaultValues: { caja_id: '', fondo_usd: '0', fondo_bs: '0' } });
  const [cajaId, fondoUsd, fondoBs] = useWatch({ control: form.control, name: ['caja_id', 'fondo_usd', 'fondo_bs'] });
  const cajas = mia.data?.cajas ?? [];
  const cajaElegida = cajas.find((c) => c.id === cajaId);
  const igual = (a: string | undefined, b: string | undefined) => {
    try {
      return Money.parse(normalizarDecimal(a ?? '0') || '0').eq(Money.parse(b ?? '0'));
    } catch {
      return true;
    }
  };
  const fondoDistinto = !!cajaElegida && (!igual(fondoUsd, cajaElegida.fondo_usd) || !igual(fondoBs, cajaElegida.fondo_bs));
  const abrir = useMutation({
    mutationFn: (datos: AbrirCajaInput) => post<SesionCajaDTO>('/caja/sesiones/abrir', datos),
    onSuccess: async (s) => {
      toast.success(`${s.caja_nombre} abierta · turno ${s.turno}`, { description: `Fondo $ ${Money.parse(s.fondo_usd).toDisplay()} · Bs ${Money.parse(s.fondo_bs).toDisplay()}${s.fondo_distinto ? ' · distinto al fondo fijo: se avisó a la dueña' : ''}` });
      form.reset();
      await Promise.all([qc.invalidateQueries({ queryKey: ['caja'] }), qc.invalidateQueries({ queryKey: ['cierres'] }), recargar()]);
      alAbrir?.(s);
    },
    onError: (e) => toast.error('No se pudo abrir la caja', { description: mensajeDeError(e) }),
  });
  return (
    <form className="space-y-3 text-left" data-testid="abrir-caja-form" onSubmit={form.handleSubmit((d) => abrir.mutate(d))} noValidate>
      <Campo etiqueta="Caja" htmlFor="ac-caja" error={form.formState.errors.caja_id?.message}>
        <Select
          id="ac-caja"
          data-testid="abrir-caja-caja"
          {...form.register('caja_id', {
            // Al elegir la caja se propone su fondo fijo (el que quedó del turno anterior)
            onChange: (e: { target: { value: string } }) => {
              const c = cajas.find((x) => x.id === e.target.value);
              form.setValue('fondo_usd', c?.fondo_usd ?? '0');
              form.setValue('fondo_bs', c?.fondo_bs ?? '0');
            },
          })}
          aria-invalid={!!form.formState.errors.caja_id}
        >
          <option value="">Selecciona una caja…</option>
          {cajas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </Select>
      </Campo>
      <div className="grid grid-cols-2 gap-3">
        <Campo etiqueta="Fondo inicial $" htmlFor="ac-fondo-usd" error={form.formState.errors.fondo_usd?.message} ayuda="El fondo fijo de la caja">
          <Input id="ac-fondo-usd" inputMode="decimal" data-testid="abrir-caja-fondo-usd" {...form.register('fondo_usd', { setValueAs: (v: string) => normalizarDecimal(v ?? '') })} aria-invalid={!!form.formState.errors.fondo_usd} />
        </Campo>
        <Campo etiqueta="Fondo inicial Bs" htmlFor="ac-fondo-bs" error={form.formState.errors.fondo_bs?.message} ayuda="Confírmalo contando lo que hay">
          <Input id="ac-fondo-bs" inputMode="decimal" data-testid="abrir-caja-fondo-bs" {...form.register('fondo_bs', { setValueAs: (v: string) => normalizarDecimal(v ?? '') })} aria-invalid={!!form.formState.errors.fondo_bs} />
        </Campo>
      </div>
      {cajaElegida ? (
        <p className={`text-xs ${fondoDistinto ? 'text-mg-ambar' : 'text-muted-foreground'}`} data-testid="abrir-caja-fondo-fijo">
          Fondo fijo de {cajaElegida.nombre}: $ {Money.parse(cajaElegida.fondo_usd ?? '0').toDisplay()} · Bs {Money.parse(cajaElegida.fondo_bs ?? '0').toDisplay()}.
          {fondoDistinto ? ' Estás abriendo con otro monto: se avisará a la dueña.' : ' Cuenta lo que hay en la gaveta; si es otra cosa, escríbelo.'}
          {cajaElegida.modalidad === 'solo_fiscal' ? ' Esta caja emite solo facturas.' : ''}
        </p>
      ) : null}
      <Button type="submit" cargando={abrir.isPending} data-testid="abrir-caja-confirmar">
        <Wallet /> Abrir caja y empezar a vender
      </Button>
    </form>
  );
}

export function DialogoAbrirCaja({ abierto, cerrar }: { abierto: boolean; cerrar: () => void }) {
  return (
    <Dialog open={abierto} onOpenChange={(o) => { if (!o) cerrar(); }}>
      <DialogContent className="max-w-md" data-testid="dialogo-abrir-caja">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>Abrir caja</DialogTitle><AyudaVentana tema="caja.abrir" /></div>
          <DialogDescription>Elige tu caja y confirma el fondo con el que empiezas. Lo que tienes en el carrito no se pierde.</DialogDescription>
        </DialogHeader>
        <FormAbrirCaja alAbrir={cerrar} />
      </DialogContent>
    </Dialog>
  );
}

/**
 * La caja abierta es de UNA sucursal. Si la dueña cambió de sucursal sin cerrarla, aquí no la usa ni la cierra: se le
 * dice dónde quedó y se le ofrece ir (reporte del 11-10-2026: la veía como «Mi sesión» de la otra tienda, con el
 * mismo nombre de caja, y al terminar el cierre el servidor respondía que no existía).
 */
export function AvisoCajaOtraSucursal() {
  const { usuario } = useAuth();
  const o = usuario?.caja_en_otra_sucursal;
  if (!usuario || !o) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert" data-testid="caja-otra-sucursal">
      <TriangleAlert className="size-4 shrink-0" />
      <span className="flex-1">
        <b>Tienes abierta la {o.caja_nombre} de {o.sucursal_nombre}</b> desde el {formatearFechaHora(o.abierta_en)}{' '}
        {o.declarado ? 'Ya declaraste su conteo: falta terminar el cierre. ' : ''}
        {usuario.sucursal_nombre ? `En ${usuario.sucursal_nombre} no puedes cobrar ni abrir otra caja hasta cerrarla. ` : ''}
        Se cuenta y se cierra operando {o.sucursal_nombre}.
      </span>
      {usuario.rol === 'superadmin' ? (
        <Button size="sm" onClick={() => pedirCambioDeSucursal({ id: o.sucursal_id, nombre: o.sucursal_nombre })} data-testid="caja-otra-sucursal-ir">
          <Building2 /> Cambiar a {o.sucursal_nombre}
        </Button>
      ) : null}
    </div>
  );
}

/** ¿La caja quedó abierta de un día anterior? El servidor no deja vender con ella hasta cerrarla. */
export function cajaDeOtroDia(abiertaEn: string | undefined): boolean {
  return !!abiertaEn && fechaCaracas(abiertaEn) < fechaCaracas(new Date());
}

/**
 * Franja del punto de venta: cómo está la caja y qué sigue. Verde con la caja abierta (y los accesos a retirar
 * efectivo y a contar y cerrar); ámbar sin caja (se puede cotizar o vender a crédito, y abrirla ahí mismo); roja si
 * la caja quedó abierta de otro día (hay que cerrarla antes de vender).
 */
export function BarraCaja({ alAbrir, alIrACaja }: { alAbrir: () => void; alIrACaja: () => void }) {
  const { usuario, puede } = useAuth();
  const s = usuario?.sesion_caja;
  if (!s && usuario?.caja_en_otra_sucursal) return <AvisoCajaOtraSucursal />;
  if (!s) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert" data-testid="pos-caja-cerrada">
        <TriangleAlert className="size-4 shrink-0" />
        <span className="flex-1"><b>No tienes una caja abierta.</b> Sin caja puedes armar la venta y vender a crédito, pero no cobrar. Ábrela antes de empezar.</span>
        {puede('caja.abrir', 'total') ? <Button size="sm" onClick={alAbrir} data-testid="pos-abrir-caja"><Wallet /> Abrir caja</Button> : null}
      </div>
    );
  }
  if (cajaDeOtroDia(s.abierta_en)) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900" role="alert" data-testid="pos-caja-otro-dia">
        <Lock className="size-4 shrink-0" />
        <span className="flex-1"><b>{s.caja_nombre} quedó abierta desde el {formatearFechaHora(s.abierta_en, { hour: undefined, minute: undefined })}.</b> No se puede vender hoy hasta contarla y cerrarla. Después abre la caja de hoy.</span>
        <Button size="sm" variant="destructive" onClick={alIrACaja} data-testid="pos-cerrar-caja-vieja"><Lock /> Contar y cerrar caja</Button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-mg-verde-claro px-3 py-1.5 text-xs text-mg-verde-oscuro" data-testid="pos-caja-abierta">
      <span className="size-2 rounded-full bg-mg-verde" aria-hidden />
      <span className="flex-1"><b>{s.caja_nombre} abierta</b> desde {formatearFechaHora(s.abierta_en)}{s.modalidad === 'solo_fiscal' ? ' · solo facturas' : ''}</span>
      <Button size="sm" variant="ghost" className="h-7" onClick={alIrACaja} data-testid="pos-caja-retiro"><Banknote /> Retiro de efectivo</Button>
      <Button size="sm" variant="ghost" className="h-7" onClick={alIrACaja} data-testid="pos-caja-cerrar"><Lock /> Contar y cerrar caja</Button>
    </div>
  );
}
